import { Component, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../core/auth.service';
import {
  ProfileViewService,
  ProfileViewConfig,
} from '../../core/profile-view.service';
// import { StoreSelectionService } from '../../core/store-selection.service';
import { StatItem } from '../../lib/ui';
import { ActivityItem } from '../../shared/components/ui/activity-list.component';
import { QuickAction } from '../../shared/components/ui/quick-actions.component';
import { KpiData } from '../../shared/components/dashboard/kpi-card/kpi-card.component';
import { DashboardWidgetsComponent } from '../../shared/components/dashboard/dashboard-widgets.component';
import { PWAService } from '../../core/pwa.service';
import { ShareButtonComponent } from '../../shared/components/share/share-button.component';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: true,
  imports: [CommonModule, DashboardWidgetsComponent, ShareButtonComponent],
})
export class DashboardComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Inject dependencies using Angular 20 pattern
  private authService = inject(AuthService);
  private router = inject(Router);
  private profileViewService = inject(ProfileViewService);

  // Inject PWA service for modern features
  private pwaService = inject(PWAService);

  // Use signals for reactive state management
  username = signal('');
  userRoles = signal<string[]>([]);
  currentProfile = signal<ProfileViewConfig | null>(null);
  selectedStoresCount = signal(0);
  isLoading = signal(true);

  stats = signal<StatItem[]>([]);
  activities = signal<ActivityItem[]>([]);
  quickActions = signal<QuickAction[]>([]);
  kpiData = signal<KpiData[]>([]);

  // Widget dashboard state
  showWidgetDashboard = signal(false);
  userPreference = signal<'traditional' | 'widgets'>('traditional');

  async ngOnInit() {
    this.username.set(await this.authService.getUsername());
    this.userRoles.set(await this.authService.getUserRoles());

    // Load user dashboard preference
    this.loadUserPreference();

    // Observar mudanças no perfil ativo
    this.profileViewService
      .getCurrentViewProfile()
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (role) => {
        this.currentProfile.set(
          this.profileViewService.getCurrentProfileConfig()
        );
        await this.loadDashboardData();
      });

    // Observar mudanças na seleção de lojas
    /*
    this.storeSelectionService
      .getSelectedStores()
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (stores) => {
        this.selectedStoresCount = stores.length;
        await this.loadDashboardData();
      });
    */

    // Carregar dados inicial
    this.currentProfile.set(this.profileViewService.getCurrentProfileConfig());
    await this.loadDashboardData();

    // Load user preference on init
    this.loadUserPreference();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getWelcomeMessage(): string {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bom dia';
    if (hour < 18) return 'Boa tarde';
    return 'Boa noite';
  }

  navigateTo(route: string) {
    this.router.navigate([route]);
  }

  onQuickActionClick(action: QuickAction): void {
    this.router.navigate([action.route]);
  }

  private async loadDashboardData() {
    if (!this.currentProfile()) return;

    const config = this.currentProfile()!.dashboardConfig;
    const role = this.currentProfile()!.role;

    // Start loading
    this.isLoading.set(true);

    // Limpar dados existentes
    this.stats.set([]);
    this.activities.set([]);
    this.quickActions.set([]);

    // Carregar dados específicos por perfil
    switch (role) {
      case 'ESTOKAI_OWNER':
        await this.loadEstokaiOwnerData(config);
        break;
      case 'dono':
        await this.loadDonoData(config);
        break;
      case 'gerente':
        await this.loadGerenteData(config);
        break;
      case 'vendedor':
        await this.loadVendedorData(config);
        break;
    }

    // End loading
    this.isLoading.set(false);
  }

  private async loadEstokaiOwnerData(config: any) {
    // Dashboard para ESTOKAI_OWNER - Visão completa do sistema

    // Novos KPI Cards
    this.kpiData.set([
      {
        title: 'Total de Lojas',
        value: 15,
        previousValue: 13,
        change: 15.4,
        changeType: 'increase',
        icon: 'store',
        color: '#1976d2',
        trend: [10, 11, 12, 13, 14, 15],
        suffix: 'ativas',
      },
      {
        title: 'Faturamento Mensal',
        value: 'R$ 2.450.000',
        previousValue: 'R$ 2.070.000',
        change: 18.4,
        changeType: 'increase',
        icon: 'attach_money',
        color: '#4caf50',
        trend: [2000000, 2100000, 2200000, 2300000, 2400000, 2450000],
      },
      {
        title: 'Usuários Ativos',
        value: 127,
        previousValue: 113,
        change: 12.4,
        changeType: 'increase',
        icon: 'people',
        color: '#ff9800',
        trend: [100, 105, 110, 115, 120, 127],
      },
      {
        title: 'Taxa de Satisfação',
        value: '94%',
        previousValue: '91%',
        change: 3.3,
        changeType: 'increase',
        icon: 'sentiment_very_satisfied',
        color: '#9c27b0',
        trend: [88, 89, 91, 92, 93, 94],
      },
    ]);

    // Stats antigos (manter para compatibilidade)
    this.stats.set([
      {
        title: 'Total de Lojas',
        subtitle: 'Ativas no sistema',
        value: 15,
        icon: 'store',
        color: 'primary',
        change: '+2',
        changeIcon: 'trending_up',
        changeClass: 'positive',
      },
      {
        title: 'Faturamento Total',
        subtitle: 'Todas as lojas',
        value: 'R$ 2.450.000',
        icon: 'attach_money',
        color: 'accent',
        change: '+18%',
        changeIcon: 'trending_up',
        changeClass: 'positive',
      },
      {
        title: 'Usuários Ativos',
        subtitle: 'Em todas as lojas',
        value: 127,
        icon: 'people',
        color: 'primary',
        change: '+12',
        changeIcon: 'trending_up',
        changeClass: 'positive',
      },
      {
        title: 'Performance Geral',
        subtitle: 'Índice de satisfação',
        value: '94%',
        icon: 'trending_up',
        color: 'accent',
        change: '+3%',
        changeIcon: 'trending_up',
        changeClass: 'positive',
      },
    ]);

    this.quickActions.set([
      {
        title: 'Gerenciar Lojas',
        description: 'Visualizar e gerenciar todas as lojas',
        icon: 'store',
        route: '/stores',
      },
      {
        title: 'Analytics Global',
        description: 'Relatórios e análises completas',
        icon: 'analytics',
        route: '/analytics',
      },
      {
        title: 'Billing',
        description: 'Gerenciar cobranças e planos',
        icon: 'receipt',
        route: '/billing',
      },
      {
        title: 'Configurações',
        description: 'Configurações do sistema',
        icon: 'settings',
        route: '/settings',
      },
    ]);

    // Atividades para ESTOKAI_OWNER
    this.activities.set([
      {
        id: '1',
        title: 'Nova loja adicionada',
        description: 'Loja do Centro - São Paulo cadastrada',
        icon: 'store',
        iconType: 'success',
        timestamp: 'Há 2 horas',
        route: '/stores',
      },
      {
        id: '2',
        title: 'Relatório mensal gerado',
        description: 'Relatório consolidado de todas as lojas',
        icon: 'assessment',
        iconType: 'primary',
        timestamp: 'Há 1 dia',
        route: '/reports',
      },
      {
        id: '3',
        title: 'Atualização do sistema',
        description: 'Versão 2.4.1 implantada com sucesso',
        icon: 'system_update',
        iconType: 'accent',
        timestamp: 'Há 2 dias',
        route: '/updates',
      },
    ]);
  }

  private async loadDonoData(config: any) {
    // Dashboard para DONO - Visão das lojas selecionadas
    // const selectedStores = await this.storeSelectionService.getSelectedStores().pipe(takeUntil(this.destroy$)).toPromise() || [];
    // const storeStats = this.storeSelectionService.getSelectedStoresStats();

    // Mock data temporário
    const storeStats = {
      totalSales: 645000,
      totalProducts: 3800,
      totalEmployees: 28,
    };

    this.stats.set([
      {
        title: 'Lojas Selecionadas',
        subtitle: `4 de 4 lojas`, // selectedStores.length} de 4 lojas`,
        value: 4, // selectedStores.length,
        icon: 'store',
        color: 'primary',
        change: '',
        changeIcon: '',
        changeClass: 'neutral',
      },
      {
        title: 'Faturamento',
        subtitle: 'Lojas selecionadas',
        value: this.formatCurrency(storeStats.totalSales),
        icon: 'attach_money',
        color: 'accent',
        change: '+18%',
        changeIcon: 'trending_up',
        changeClass: 'positive',
      },
      {
        title: 'Produtos',
        subtitle: 'Total em estoque',
        value: storeStats.totalProducts,
        icon: 'inventory_2',
        color: 'primary',
        change: '+12%',
        changeIcon: 'trending_up',
        changeClass: 'positive',
      },
      {
        title: 'Funcionários',
        subtitle: 'Total nas lojas',
        value: storeStats.totalEmployees,
        icon: 'people',
        color: 'accent',
        change: '+2',
        changeIcon: 'trending_up',
        changeClass: 'positive',
      },
    ]);

    this.quickActions.set([
      {
        title: 'Comparar Lojas',
        description: 'Análise comparativa entre lojas',
        icon: 'compare_arrows',
        route: '/store-comparison',
      },
      {
        title: 'Relatórios Consolidados',
        description: 'Relatórios das lojas selecionadas',
        icon: 'assessment',
        route: '/reports',
      },
      {
        title: 'Gerenciar Usuários',
        description: 'Funcionários de todas as lojas',
        icon: 'manage_accounts',
        route: '/users',
      },
      {
        title: 'Configurações',
        description: 'Configurações das lojas',
        icon: 'settings',
        route: '/settings',
      },
    ]);

    // Atividades para DONO
    this.activities.set([
      {
        id: '1',
        title: 'Vendas acima da meta',
        description: 'Loja Shopping bateu recorde mensal',
        icon: 'celebration',
        iconType: 'success',
        timestamp: 'Há 30 min',
        route: '/reports',
      },
      {
        id: '2',
        title: 'Novo funcionário',
        description: 'João Silva contratado como vendedor',
        icon: 'person_add',
        iconType: 'primary',
        timestamp: 'Há 2 horas',
        route: '/users',
      },
      {
        id: '3',
        title: 'Estoque baixo',
        description: 'iPhone 15 precisa de reposição',
        icon: 'warning',
        iconType: 'warning',
        timestamp: 'Há 4 horas',
        route: '/products',
      },
    ]);
  }

  private async loadGerenteData(config: any) {
    // Dashboard para GERENTE - Visão operacional
    this.stats.set([
      {
        title: 'Vendas Hoje',
        subtitle: 'Até agora',
        value: 23,
        icon: 'shopping_cart',
        color: 'accent',
        change: '+15%',
        changeIcon: 'trending_up',
        changeClass: 'positive',
      },
      {
        title: 'Meta do Mês',
        subtitle: '75% atingida',
        value: 'R$ 45.280',
        icon: 'flag',
        color: 'primary',
        change: 'R$ 15.000 restantes',
        changeIcon: 'target',
        changeClass: 'neutral',
      },
      {
        title: 'Produtos em Falta',
        subtitle: 'Atenção necessária',
        value: 8,
        icon: 'warning',
        color: 'warn',
        change: '+2',
        changeIcon: 'error',
        changeClass: 'negative',
      },
      {
        title: 'Funcionários',
        subtitle: 'Presentes hoje',
        value: '7/8',
        icon: 'people',
        color: 'primary',
        change: '1 ausente',
        changeIcon: 'person_off',
        changeClass: 'neutral',
      },
    ]);

    this.quickActions.set([
      {
        title: 'Controle de Vendas',
        description: 'Acompanhar vendas do dia',
        icon: 'point_of_sale',
        route: '/sales',
      },
      {
        title: 'Gestão de Estoque',
        description: 'Verificar e repor produtos',
        icon: 'inventory_2',
        route: '/products',
      },
      {
        title: 'Equipe',
        description: 'Gerenciar funcionários',
        icon: 'group',
        route: '/team',
      },
      {
        title: 'Relatórios',
        description: 'Análises operacionais',
        icon: 'assessment',
        route: '/reports',
      },
    ]);

    // Atividades para GERENTE
    this.activities.set([
      {
        id: '1',
        title: 'Meta diária atingida',
        description: 'Equipe vendeu R$ 8.500 hoje',
        icon: 'flag',
        iconType: 'success',
        timestamp: 'Há 1 hora',
        route: '/sales',
      },
      {
        id: '2',
        title: 'Produto em falta',
        description: 'Notebook Dell precisa ser reposto',
        icon: 'inventory_2',
        iconType: 'warning',
        timestamp: 'Há 2 horas',
        route: '/products',
      },
      {
        id: '3',
        title: 'Funcionário do mês',
        description: 'Ana Santos bateu recorde de vendas',
        icon: 'star',
        iconType: 'accent',
        timestamp: 'Há 1 dia',
        route: '/team',
      },
    ]);
  }

  private async loadVendedorData(config: any) {
    // Dashboard para VENDEDOR - Foco em vendas pessoais
    this.stats.set([
      {
        title: 'Minhas Vendas Hoje',
        subtitle: 'Vendas realizadas',
        value: 8,
        icon: 'shopping_cart',
        color: 'accent',
        change: '+3 vs ontem',
        changeIcon: 'trending_up',
        changeClass: 'positive',
      },
      {
        title: 'Comissão do Mês',
        subtitle: 'Ganhos até agora',
        value: 'R$ 1.240',
        icon: 'monetization_on',
        color: 'primary',
        change: '+22%',
        changeIcon: 'trending_up',
        changeClass: 'positive',
      },
      {
        title: 'Meta Pessoal',
        subtitle: '68% atingida',
        value: '34/50',
        icon: 'flag',
        color: 'primary',
        change: '16 vendas restantes',
        changeIcon: 'target',
        changeClass: 'neutral',
      },
      {
        title: 'Clientes Atendidos',
        subtitle: 'Hoje',
        value: 15,
        icon: 'people_outline',
        color: 'accent',
        change: '+5',
        changeIcon: 'trending_up',
        changeClass: 'positive',
      },
    ]);

    this.quickActions.set([
      {
        title: 'Nova Venda',
        description: 'Registrar venda',
        icon: 'add_shopping_cart',
        route: '/sales/new',
      },
      {
        title: 'Consultar Produtos',
        description: 'Verificar disponibilidade',
        icon: 'search',
        route: '/products',
      },
      {
        title: 'Meu Desempenho',
        description: 'Acompanhar metas',
        icon: 'timeline',
        route: '/my-performance',
      },
      {
        title: 'Treinamentos',
        description: 'Materiais de apoio',
        icon: 'school',
        route: '/training',
      },
    ]);

    // Atividades específicas do vendedor
    this.activities.set([
      {
        id: '1',
        title: 'Venda realizada',
        description: 'Notebook Lenovo - R$ 2.500,00',
        icon: 'shopping_cart',
        iconType: 'success',
        timestamp: 'Há 15 min',
        route: '/sales/latest',
      },
      {
        id: '2',
        title: 'Cliente consultado',
        description: 'Maria Silva - Interessada em smartphones',
        icon: 'person',
        iconType: 'primary',
        timestamp: 'Há 30 min',
        route: '/customers',
      },
      {
        id: '3',
        title: 'Meta alcançada!',
        description: 'Você atingiu 70% da meta mensal',
        icon: 'celebration',
        iconType: 'accent',
        timestamp: 'Há 1 hora',
        route: '/my-performance',
      },
    ]);
  }

  // Check if user has seen widgets dashboard
  hasSeenWidgets(): boolean {
    return (
      localStorage.getItem('estokai_seen_widgets') === 'true' ||
      this.showWidgetDashboard()
    );
  }

  // Switch to widget dashboard
  switchToWidgets(): void {
    this.showWidgetDashboard.set(true);
    this.userPreference.set('widgets');
    localStorage.setItem('estokai_seen_widgets', 'true');
    localStorage.setItem('estokai_dashboard_preference', 'widgets');
  }

  // Switch back to traditional dashboard
  switchToTraditional(): void {
    this.showWidgetDashboard.set(false);
    this.userPreference.set('traditional');
    localStorage.setItem('estokai_dashboard_preference', 'traditional');
  }

  // Load user preference on init
  private loadUserPreference(): void {
    const savedPreference = localStorage.getItem(
      'estokai_dashboard_preference'
    ) as 'traditional' | 'widgets';
    const hasSeenWidgets =
      localStorage.getItem('estokai_seen_widgets') === 'true';

    if (savedPreference) {
      this.userPreference.set(savedPreference);
      this.showWidgetDashboard.set(savedPreference === 'widgets');
    } else if (hasSeenWidgets) {
      this.showWidgetDashboard.set(true);
      this.userPreference.set('widgets');
    }
  }

  private formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  }
}
